import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import styles from './RecallReviewView.module.scss'
import { useMutation } from '@apollo/client'
import UserContext from '../context/UserContext'
import { SET_WORD_IMAGE_MUTATION } from '../query/gqlQueries'
import { streamOllamaGenerate } from '../ollama'
import { uploadImageToStore } from '../imageStore'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
const STUCK_BEFORE_ACCEPT = 6

const stripGloss = (s) => String(s || '').replace(/\(([^)]*[A-Za-z][^)]*)\)/g, '').replace(/\s{2,}/g, ' ').trim()

// Reduce a card to its practisable single word + English meaning + any image.
const wordItemOf = (card) => {
  const t = card.translations?.[0]
  let word, lang, meaning
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    word = stripGloss(card.source); lang = card.sourceLanguage; meaning = t?.target || ''
  } else if (t?.target && t.targetLanguage !== 'eng') {
    word = stripGloss(t.target); lang = t.targetLanguage; meaning = card.source || ''
  } else return null
  if (!word || /\s/.test(word)) return null // single word only
  return {
    word, lang, meaning,
    imageUrl: card.sourceImageUrl || null,
    norm: word.normalize('NFC').toLowerCase(),
  }
}

export default function RecallReviewView({ cards, difficultNorms, voiceSpeed, onClose, onBack }) {
  const { username } = useContext(UserContext)
  const [setWordImage] = useMutation(SET_WORD_IMAGE_MUTATION)

  // Build the word list: dedupe, and float the words the learner struggled with
  // (fuzzy match, since sentence tokens may carry particles) to the front.
  const items = useMemo(() => {
    const diff = new Set(difficultNorms || [])
    const isHard = (norm) => [...diff].some(d => d.startsWith(norm) || norm.startsWith(d))
    const seen = new Set()
    const out = []
    for (const c of (cards || [])) {
      const it = wordItemOf(c)
      if (!it || seen.has(it.norm)) continue
      seen.add(it.norm)
      out.push({ ...it, hard: isHard(it.norm) })
    }
    out.sort((a, b) => (b.hard ? 1 : 0) - (a.hard ? 1 : 0))
    return out
  }, [cards, difficultNorms])

  const [idx, setIdx] = useState(0)
  const [done, setDone] = useState(false)
  const [hits, setHits] = useState({ forIdx: -1, states: [] })
  const [revealed, setRevealed] = useState(false)       // word text + image shown
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micError, setMicError] = useState(null)
  const [images, setImages] = useState({})               // word|lang -> url (freshly generated)
  const [imgStatus, setImgStatus] = useState('idle')     // idle | working | error
  const [imgMsg, setImgMsg] = useState('')

  const item = items[idx]
  const targetTokens = useMemo(() => tokenizeLine(item?.word, item?.lang).map(t => t.norm), [item])
  const states = hits.forIdx === idx ? hits.states : []
  const roman = romanizationOf(item?.word, item?.lang)
  const imgKey = item ? `${item.word}|${item.lang}` : ''
  const currentImage = images[imgKey] ?? item?.imageUrl ?? null

  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const missRef = useRef(0)
  const lastFinalRef = useRef('')
  const liveHitsRef = useRef([])
  const itemRef = useRef(null)
  const cardRef = useRef(null)
  useEffect(() => { itemRef.current = item }, [item])

  // Reset per word.
  useEffect(() => {
    if (!item) return
    const fresh = targetTokens.map(() => false)
    liveHitsRef.current = fresh
    setHits({ forIdx: idx, states: fresh })
    setRevealed(false)
    setInterim('')
    setImgStatus('idle'); setImgMsg('')
    completedRef.current = false
    missRef.current = 0
    lastFinalRef.current = ''
  }, [idx, item, targetTokens])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      if (prev + 1 >= items.length) { setDone(true); return prev }
      return prev + 1
    })
  }, [items.length])

  const applyTranscript = useCallback((transcript, isFinal) => {
    if (!itemRef.current) return
    const tokens = tokenizeTranscript(transcript, itemRef.current.lang)
    const base = liveHitsRef.current.length === targetTokens.length ? liveHitsRef.current : targetTokens.map(() => false)
    const next = [...base]
    let added = 0
    for (let ti = 0; ti < targetTokens.length; ti++) {
      if (next[ti]) continue
      if (tokens.some(tok => tokenMatches(tok, targetTokens[ti], itemRef.current?.lang))) { next[ti] = true; added++ }
    }
    liveHitsRef.current = next
    setHits({ forIdx: idx, states: next })

    if (isFinal && !completedRef.current) {
      const heard = transcript.trim()
      if (heard && heard !== lastFinalRef.current) {
        lastFinalRef.current = heard
        if (added > 0) { missRef.current = 0 }
        else {
          missRef.current += 1
          if (missRef.current >= STUCK_BEFORE_ACCEPT) {
            missRef.current = 0
            const t = next.findIndex(h => !h)
            if (t >= 0) { next[t] = true; liveHitsRef.current = next; setHits({ forIdx: idx, states: next }) }
          }
        }
      }
    }
  }, [idx, targetTokens])

  // All tokens recalled → celebrate → next.
  useEffect(() => {
    if (hits.forIdx !== idx || !item) return
    if (targetTokens.length === 0 || hits.states.length !== targetTokens.length) return
    if (!hits.states.every(Boolean) || completedRef.current) return
    completedRef.current = true
    setRevealed(true)
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 22, spread: 150 }) }
    advanceTimerRef.current = setTimeout(advance, 1200)
  }, [hits, idx, item, targetTokens.length, advance])

  // ── recognizer (compact port) ────────────────────────────────────────────
  const stopListening = useCallback(() => {
    listeningRef.current = false; setListening(false); sessionRef.current++
    try { recRef.current?.stop() } catch { /* stopped */ }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) { setMicError('Speech recognition is not supported in this browser.'); return }
    if (!itemRef.current) return
    setMicError(null)
    cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[itemRef.current.lang] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    let heardSound = false
    const stopWithError = (msg) => { listeningRef.current = false; setListening(false); setMicError(msg) }
    rec.onsoundstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onspeechstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true; deadSessionsRef.current = 0
      let transcript = '', sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) sawFinal = true
      }
      setInterim(transcript.trim().slice(-60))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current || completedRef.current) return
      if (!heardSound) { deadSessionsRef.current++; if (deadSessionsRef.current >= 4) { stopWithError('Mic is open but no sound is reaching it — check the input in the address-bar site settings.'); return } }
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* raced */ } } }, 500)
    }
    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try { rec.start() } catch {
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* give up */ } } }, 400)
    }
  }, [applyTranscript])

  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 350)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    listeningRef.current = false
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  // Tap the word (or the hear button): play it AND reveal it + its image. This
  // is the "after you tap to hear it" moment the image is meant to appear.
  const hearAndReveal = () => {
    if (!item) return
    speak(item.word, voices[item.lang] || voices['eng'], voiceSpeed)
    setRevealed(true)
  }

  const generateImage = useCallback(async () => {
    if (!item) return
    const sdUrl = process.env.REACT_APP_IMAGE_GEN_URI
    const sdKey = process.env.REACT_APP_IMAGE_GEN_KEY
    const ollamaUrl = process.env.REACT_APP_OLLAMA_URI
    if (!sdUrl) { setImgStatus('error'); setImgMsg('Image generator (REACT_APP_IMAGE_GEN_URI) is not configured.'); return }
    if (!username) { setImgStatus('error'); setImgMsg('No user — cannot save the image.'); return }
    setImgStatus('working'); setImgMsg('Inventing a memory hook…')

    const langName = languages[item.lang] || item.lang
    const promptOllama = `You are designing a single-scene image that helps an English speaker remember a ${langName} vocabulary word using a visual memory hook.

Word: ${item.word}
Meaning: ${item.meaning || '(unknown)'}

Output a single vivid Stable Diffusion prompt (under 30 words) combining a literal depiction of the meaning AND a visual pun on how the word sounds to an English speaker. Output ONLY the prompt, no quotes or preamble.`
    let sdPrompt = ''
    try {
      sdPrompt = await streamOllamaGenerate({
        serverUrl: ollamaUrl,
        apiKey: process.env.REACT_APP_OLLAMA_KEY,
        body: { model: 'qwen2.5:14b', prompt: promptOllama, options: { temperature: 0.6, num_predict: 120 } },
      })
      sdPrompt = (sdPrompt || '').replace(/^["'`]+|["'`]+$/g, '').trim()
    } catch { sdPrompt = '' }
    if (!sdPrompt) sdPrompt = `minimal flat illustration of ${item.word} (${item.meaning || ''})`

    setImgMsg('Drawing…')
    let base64
    try {
      const finalPrompt = `${sdPrompt}, minimal flat illustration, single centered subject, clean vector-style artwork, bold solid colors, pastel background, no text, no letters, no words`
      const res = await fetch(`${sdUrl}/sdapi/v1/txt2img`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': sdKey || '' },
        body: JSON.stringify({
          prompt: finalPrompt,
          negative_prompt: 'text, letters, words, typography, watermark, signature, numbers, cluttered background, photorealistic, blurry, low quality, nsfw',
          steps: 8, width: 512, height: 512, cfg_scale: 4,
        }),
      })
      if (!res.ok) throw new Error(`SD HTTP ${res.status}`)
      const data = await res.json()
      base64 = data.images?.[0]
      if (!base64) throw new Error('no image')
    } catch (e) { setImgStatus('error'); setImgMsg('Image generation failed.'); return }

    setImgMsg('Saving…')
    try {
      const uploadedUrl = await uploadImageToStore({ kind: 'word', text: item.word, lang: item.lang, username, imageBase64: base64 })
      const { data } = await setWordImage({ variables: { text: item.word, lang: item.lang, username, imageUrl: uploadedUrl, description: null } })
      const savedUrl = data?.setWordImage?.imageUrl || uploadedUrl
      setImages(prev => ({ ...prev, [`${item.word}|${item.lang}`]: savedUrl }))
      setImgStatus('idle'); setImgMsg('')
    } catch (e) { setImgStatus('error'); setImgMsg('Could not save the image.') }
  }, [item, username, setWordImage])

  // ── render ────────────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Review</div>
          <div className={styles.doneSub}>No single-word vocabulary in this set to review.</div>
          <button className={styles.primaryBtn} onClick={onBack || onClose}>Back</button>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Review complete</div>
          <div className={styles.doneTitle}>★ Nice work</div>
          <div className={styles.doneSub}>You reviewed {items.length} word{items.length === 1 ? '' : 's'}.</div>
          <div className={styles.doneActions}>
            <button className={styles.ghostBtn} onClick={() => { setIdx(0); setDone(false) }}>Again</button>
            <button className={styles.primaryBtn} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    )
  }

  const recLangLabel = recognitionLangs[item?.lang] || 'en-US'
  const gotIt = states.length > 0 && states.every(Boolean)

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
      <div className={styles.topBar}>
        <span className={styles.modeLabel}>Review · {languages[item?.lang] || item?.lang}</span>
        {item?.hard && <span className={styles.hardTag}>tricky</span>}
      </div>

      <div className={styles.stage}>
        <div className={styles.promptLabel}>what's this in {languages[item?.lang] || item?.lang}?</div>
        <div className={styles.prompt}>{item?.meaning || '—'}</div>

        <div className={styles.card} ref={cardRef}>
          {/* image appears once revealed (after you tap to hear) */}
          {revealed && (
            currentImage ? (
              <img className={styles.image} src={currentImage} alt={`mnemonic for ${item?.word}`} />
            ) : imgStatus === 'working' ? (
              <div className={styles.imagePlaceholder}><div className={styles.spinner} /><div className={styles.imgMsg}>{imgMsg}</div></div>
            ) : (
              <button className={styles.genBtn} onClick={generateImage}>🎨 Generate memory image</button>
            )
          )}
          {revealed && imgStatus === 'error' && <div className={styles.imgErr}>{imgMsg}</div>}

          <div className={`${styles.word} ${gotIt ? styles.wordHit : ''} ${!revealed ? styles.wordHidden : ''}`} onClick={hearAndReveal}>
            {revealed || gotIt ? item?.word : '＿＿＿'}
          </div>
          {(revealed || gotIt) && roman && <div className={styles.roman}>{roman}</div>}
        </div>

        <div className={styles.tip}>tap the word to hear it &amp; reveal its picture</div>
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.interim}>
          {micError ? micError : listening ? (interim || `mic open — say it in ${languages[item?.lang] || item?.lang}`) : 'say the word out loud'}
        </div>
        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hearAndReveal} title="Hear it & reveal"><VolumeUpIcon /></button>
          <button
            className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`}
            onClick={() => (listening ? stopListening() : startListening())}
            title={listening ? 'Stop the mic' : 'Say the word'}
          >
            {listening ? <MicIcon style={{ fontSize: 32 }} /> : <MicOffIcon style={{ fontSize: 32 }} />}
          </button>
          <button className={styles.sideBtn} onClick={advance} title="Skip / next"><NavigateNextIcon /></button>
        </div>
        <div className={styles.progress}>{idx + 1} / {items.length}</div>
      </div>
    </div>
  )
}
